<template>
  <div class="pt-broadcast-page">
    <!-- 격자 영역 -->
    <pt-grid
      class="pt-broadcast-page__grid"
      :cells="config.cells"
      :cells-by-lang="vmState.cellsByLang"
      :layout="config.layout"
      :show-speaker-strip="config.showSpeakerStrip"
      :source-preview="vmState.sourcePreview"
      :source-lang="config.sourceLang"
      :font-scale-global="config.fontScaleGlobal"
      :show-model-badge="config.showModelBadge"
      :direction="direction"
    />

    <!-- 컨트롤바 -->
    <pt-control-bar
      :is-connecting="vmState.isConnecting"
      :is-streaming="vmState.isStreaming"
      :is-paused="vmState.isPaused"
      :elapsed-sec="vmState.elapsedSec"
      :error="vmState.error"
      :auto-downgraded="vmState.autoDowngradedToStt"
      :direction="direction"
      :pt-mode="effectivePtMode"
      @start="onStart"
      @pause="onPause"
      @resume="onResume"
      @stop="onStop"
      @reset="onReset"
      @settings="settingsVisible = true"
      @history="openHistory"
      @fullscreen="toggleFullscreen"
      @exit="onExit"
      @toggle-direction="onToggleDirection"
    />

    <!-- 설정 드로어 -->
    <pt-settings-drawer
      :visible="settingsVisible"
      :config="config"
      @close="settingsVisible = false"
      @save="onSaveConfig"
    />
  </div>
</template>

<script>
import PtGrid from '@/components/ai-pt/PtGrid.vue'
import PtControlBar from '@/components/ai-pt/PtControlBar.vue'
import PtSettingsDrawer from '@/components/ai-pt/PtSettingsDrawer.vue'
import { useVoicePtBroadcast } from '@/composables/useVoicePtBroadcast'

function defaultConfig() {
  return {
    title: '',
    ptMode: 'stt',
    sourceLang: 'ko',
    layout: 'auto',
    fontScaleGlobal: 1.0,
    showSpeakerStrip: true,
    showModelBadge: false,
    cells: [
      { slot: 0, lang: 'en', bg: '#0F172A', fg: '#FFFFFF', fontScale: 1.0 },
      { slot: 1, lang: 'vi', bg: '#14532D', fg: '#D1FAE5', fontScale: 1.0 },
      { slot: 2, lang: 'zh', bg: '#7F1D1D', fg: '#FEE2E2', fontScale: 1.0 },
      { slot: 3, lang: 'th', bg: '#581C87', fg: '#FAE8FF', fontScale: 1.0 },
    ],
  }
}

export default {
  name: 'AiVoicePtBroadcast',
  components: { PtGrid, PtControlBar, PtSettingsDrawer },
  data() {
    return {
      // 사용자 설정 (localStorage persist)
      config: defaultConfig(),
      // 컴포저블 인스턴스 & 상태
      pt: null,
      vmState: {
        isConnecting: false,
        isStreaming: false,
        isPaused: false,
        sourcePreview: '',
        cellsByLang: {},
        elapsedSec: 0,
        error: null,
        autoDowngradedToStt: false,
        sessionId: null,
      },
      settingsVisible: false,
      /** 'newest_top' | 'newest_bottom' — 컨트롤바 토글로 변경, localStorage 영속화 */
      direction: 'newest_top',
    }
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id
    },
    storageKey() {
      const username = localStorage.getItem('login_username') || 'anon'
      return 'voice_pt_config_' + username + '_' + this.projectId
    },
    directionStorageKey() {
      const username = localStorage.getItem('login_username') || 'anon'
      return 'voice_pt_direction_' + username + '_' + this.projectId
    },
    /** 자동 다운그레이드된 경우 실제 운영 중인 모드를 컨트롤바에 표시 */
    effectivePtMode() {
      if (this.vmState.autoDowngradedToStt) return 'stt'
      return this.config.ptMode || 'stt'
    },
  },
  created() {
    this._loadConfigFromStorage()
    this._loadDirectionFromStorage()
    // 시작 전에도 격자가 빈 상태로 보이도록 cellsByLang 미리 초기화
    this._resetCellsByLang()

    this.pt = useVoicePtBroadcast({
      onStateChange: (s) => {
        // cellsByLang 은 onStateChange 가 아니라 onEvent('turn') 에서
        // 페이지가 직접 entry push 로 관리한다 (Vue 반응형이 새 entry 하나만
        // 감지하여 기존 DOM 재사용 → 깜빡임 제거).
        this.vmState.isConnecting = s.isConnecting
        this.vmState.isStreaming = s.isStreaming
        this.vmState.isPaused = s.isPaused
        this.vmState.sourcePreview = s.sourcePreview
        this.vmState.elapsedSec = s.elapsedSec
        this.vmState.error = s.error
        this.vmState.autoDowngradedToStt = s.autoDowngradedToStt
        this.vmState.sessionId = s.sessionId
      },
      onEvent: (evt) => {
        if (evt.type === 'turn') {
          this._applyTurnToVm(evt.turn)
          return
        }
        if (evt.type === 'cell_partial') {
          this._applyCellPartialToVm(evt.lang, evt.text)
          return
        }
        if (evt.type === 'cell_final') {
          this._applyCellFinalToVm(evt.lang, evt.text)
          return
        }
        if (evt.type === 'cell_error') {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: 'PT',
              text: '[' + evt.lang + '] ' + evt.message,
              color: 'warning',
              position: 'top-center',
              time: 4000,
            })
          }
          return
        }
        if (evt.type === 'mode_changed') {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: this.$t ? this.$t('자동 모드 변경') : '자동 모드 변경',
              text: evt.reason ? evt.reason : ('Mode: ' + evt.from + ' → ' + evt.to),
              color: 'warning',
              position: 'top-center',
              time: 5000,
            })
          }
        } else if (evt.type === 'error') {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: this.$t ? this.$t('오류') : '오류',
              text: evt.message,
              color: 'danger',
              position: 'top-center',
              time: 6000,
            })
          }
        } else if (evt.type === 'mic_info') {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: this.$t ? this.$t('마이크 입력 장치') : '마이크 입력 장치',
              text: evt.label,
              color: 'primary',
              position: 'top-center',
              time: 4000,
            })
          }
        } else if (evt.type === 'silence_warning') {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: this.$t ? this.$t('무음 감지') : '무음 감지',
              text: evt.message,
              color: 'warning',
              position: 'top-center',
              time: 7000,
            })
          }
        }
      },
    })

    // 권한 사전 체크
    this.pt.checkMicPermission()
  },
  beforeDestroy() {
    if (this.pt && this.vmState.isStreaming) {
      this.pt.stop()
    }
  },
  methods: {
    _loadDirectionFromStorage() {
      try {
        const v = localStorage.getItem(this.directionStorageKey)
        if (v === 'newest_bottom' || v === 'newest_top') {
          this.direction = v
        }
      } catch (e) { /* ignore */ }
    },
    _saveDirectionToStorage() {
      try {
        localStorage.setItem(this.directionStorageKey, this.direction)
      } catch (e) { /* ignore */ }
    },
    onToggleDirection() {
      this.direction = this.direction === 'newest_top' ? 'newest_bottom' : 'newest_top'
      this._saveDirectionToStorage()
    },

    _loadConfigFromStorage() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          // 누락 키 보정
          const def = defaultConfig()
          const keys = Object.keys(def)
          for (let i = 0; i < keys.length; i++) {
            if (parsed[keys[i]] === undefined) parsed[keys[i]] = def[keys[i]]
          }
          this.config = parsed
        }
      } catch (e) {
        // ignore corrupt
      }
    },
    _saveConfigToStorage() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.config))
      } catch (e) {
        // ignore quota
      }
    },

    /** 격자별 entries 누적 한도 — 사실상 무제한이지만 메모리 가드용. */
    HISTORY_MAX() { return 200 },

    _resetCellsByLang() {
      const next = {}
      for (let i = 0; i < this.config.cells.length; i++) {
        const lang = this.config.cells[i].lang
        if (!lang) continue
        if (next[lang]) continue
        next[lang] = {
          lang: lang,
          entries: [],
          lastModel: null,
          failed: false,
          updatedAt: null,
        }
      }
      this.vmState.cellsByLang = next
    },

    /**
     * ModeA(Live × N): 격자별 진행 중 통역 텍스트 (덮어쓰기).
     * 마지막 entry 가 in-progress 면 객체를 splice 로 교체 (Vue 2 reactivity 안전),
     * 아니면 새 in-progress entry 를 push.
     */
    _applyCellPartialToVm(lang, text) {
      const cell = this.vmState.cellsByLang[lang]
      if (!cell) return
      if (!text) return
      const lastIdx = cell.entries.length - 1
      const last = lastIdx >= 0 ? cell.entries[lastIdx] : null
      if (last && last.inProgress) {
        // 같은 id 를 유지해 transition-group DOM 재사용 + text 만 갱신.
        const updated = {
          id: last.id,
          text: text,
          model: 'live',
          failed: false,
          inProgress: true,
          at: Date.now(),
        }
        cell.entries.splice(lastIdx, 1, updated)
      } else {
        const ts = Date.now()
        cell.entries.push({
          id: 'live_' + ts + '_' + lang + '_' + Math.random().toString(36).slice(2, 6),
          text: text,
          model: 'live',
          failed: false,
          inProgress: true,
          at: ts,
        })
        const maxN = this.HISTORY_MAX()
        if (cell.entries.length > maxN) {
          cell.entries.splice(0, cell.entries.length - maxN)
        }
      }
      cell.lastModel = 'live'
      cell.failed = false
      cell.updatedAt = Date.now()
    },

    /**
     * ModeA(Live × N): 격자별 최종 통역. 마지막 in-progress entry 를 final 로 확정.
     */
    _applyCellFinalToVm(lang, text) {
      const cell = this.vmState.cellsByLang[lang]
      if (!cell) return
      if (!text) return
      const lastIdx = cell.entries.length - 1
      const last = lastIdx >= 0 ? cell.entries[lastIdx] : null
      if (last && last.inProgress) {
        const updated = {
          id: last.id,
          text: text,
          model: 'live',
          failed: false,
          inProgress: false,
          at: Date.now(),
        }
        cell.entries.splice(lastIdx, 1, updated)
      } else {
        const ts = Date.now()
        cell.entries.push({
          id: 'live_final_' + ts + '_' + lang + '_' + Math.random().toString(36).slice(2, 6),
          text: text,
          model: 'live',
          failed: false,
          inProgress: false,
          at: ts,
        })
        const maxN = this.HISTORY_MAX()
        if (cell.entries.length > maxN) {
          cell.entries.splice(0, cell.entries.length - maxN)
        }
      }
      cell.lastModel = 'live'
      cell.failed = false
      cell.updatedAt = Date.now()
    },

    _applyTurnToVm(turn) {
      // turn 이벤트 도착 시 entries.push 만 수행 — Vue 반응형이 새 entry 하나만
      // 감지하여 기존 DOM 재사용. 깜빡임/CSS 애니메이션 재발동 방지.
      if (!turn) return
      const translations = turn.translations ? turn.translations : {}
      const modelUsed = turn.model_used ? turn.model_used : {}
      const ts = Date.now()
      const turnId = turn.turn_id !== undefined && turn.turn_id !== null ? turn.turn_id : null

      const langs = Object.keys(this.vmState.cellsByLang)
      for (let i = 0; i < langs.length; i++) {
        const lang = langs[i]
        const cell = this.vmState.cellsByLang[lang]
        if (!cell) continue
        if (translations[lang] === undefined) continue

        const text = translations[lang]
        const model = modelUsed[lang] !== undefined ? modelUsed[lang] : null
        const failed = (!text) || (model === null)
        const entryId = (turnId !== null ? turnId : ('local_' + ts)) + '_' + lang + '_' + cell.entries.length

        cell.entries.push({
          id: entryId,
          text: text || '',
          model: model,
          failed: failed,
          at: ts,
        })

        // 메모리 가드 — 한도 초과 시 가장 오래된 항목들 제거
        const maxN = this.HISTORY_MAX()
        if (cell.entries.length > maxN) {
          cell.entries.splice(0, cell.entries.length - maxN)
        }
        cell.lastModel = model
        cell.failed = failed
        cell.updatedAt = ts
      }
    },

    async onStart() {
      if (!this.pt) return
      const targetLangs = []
      for (let i = 0; i < this.config.cells.length; i++) {
        const lang = this.config.cells[i].lang
        if (lang && targetLangs.indexOf(lang) < 0) targetLangs.push(lang)
      }
      if (targetLangs.length === 0) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({
            title: 'PT',
            text: this.$t ? this.$t('번역 격자를 1개 이상 추가해주세요.') : '번역 격자를 1개 이상 추가해주세요.',
            color: 'warning',
            position: 'top-center',
            time: 3000,
          })
        }
        this.settingsVisible = true
        return
      }

      // 발표 제목이 비어있으면 입력 받기. 취소 시 시작 중단.
      const currentTitle = (this.config.title || '').trim()
      if (!currentTitle) {
        const promptMsg = this.$t
          ? this.$t('발표 제목을 입력해주세요. (이후 이력 페이지에서 식별용으로 사용됩니다)')
          : '발표 제목을 입력해주세요. (이후 이력 페이지에서 식별용으로 사용됩니다)'
        let input = null
        try {
          input = window.prompt(promptMsg, '')
        } catch (e) { input = null }
        if (input === null) return  // 취소
        const trimmed = (input || '').trim()
        if (!trimmed) {
          if (this.$vs && this.$vs.notify) {
            this.$vs.notify({
              title: 'PT',
              text: this.$t ? this.$t('발표 제목이 비어있어 시작을 취소합니다.') : '발표 제목이 비어있어 시작을 취소합니다.',
              color: 'warning',
              position: 'top-center',
              time: 3000,
            })
          }
          return
        }
        this.config.title = trimmed
        this._saveConfigToStorage()
      }

      this._resetCellsByLang()
      await this.pt.start({
        title: this.config.title,
        sourceLang: this.config.sourceLang,
        targetLangs: targetLangs,
        ptMode: this.config.ptMode,
      })
    },
    onPause() { if (this.pt) this.pt.pause() },
    onResume() { if (this.pt) this.pt.resume() },
    async onStop() { if (this.pt) await this.pt.stop() },
    async onReset() {
      if (this.pt) await this.pt.reset()
      this._resetCellsByLang()
    },

    onSaveConfig(newConfig) {
      this.config = newConfig
      this._saveConfigToStorage()
      this.settingsVisible = false
    },

    openHistory() {
      const projId = this.projectId
      this.$router.push({ name: 'ai-voice-pt-history', params: { proj_id: projId } })
    },

    onExit() {
      // 풀스크린 해제 후 프로젝트 홈으로
      try {
        if (document.fullscreenElement) document.exitFullscreen()
      } catch (e) { /* ignore */ }

      const projId = this.projectId
      this.$router.push('/project/' + projId + '/home').catch(() => {})
    },

    async toggleFullscreen() {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen()
        } else {
          await document.exitFullscreen()
        }
      } catch (e) { /* ignore */ }
    },
  },
}
</script>

<style scoped>
.pt-broadcast-page {
  position: fixed;
  inset: 0;
  background: #050816;
  color: #fff;
  z-index: 9999;
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
}

.pt-broadcast-page__grid {
  min-height: 0;
}
</style>
